import { useState, useEffect } from 'react';
import { apiUrl } from '../utils/apiBase';

export interface IntegracaoConfig {
  id: string;
  nome: string;
  tipo: 'google_drive' | 'dropbox' | 'onedrive' | 'email' | 'whatsapp';
  email: string;
  pastaId?: string;
  pastaNome: string;
  pastaPath?: string;
  ativo: boolean;
  configuradoEm?: Date;
  ultimaSync?: Date;
  nomeConfigurador?: string;
  credenciais?: {
    clientId?: string;
    accessToken?: string;
  };
  emailDestino?: string;
  descricao?: string;
  criadoPor?: string;
}

export const useIntegracoes = () => {
  const [integracoes, setIntegracoes] = useState<IntegracaoConfig[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    carregarIntegracoes();
  }, []);

  const carregarIntegracoes = async () => {
    setLoading(true);
    try {
      // Recuperar sessão para coletar empresaId e userEmail
      let empresaId = '';
      let userEmail = '';
      try {
        const raw = typeof window !== 'undefined' ? localStorage.getItem('session') : null;
        console.log('🔍 useIntegracoes - Raw session:', raw);
        if (raw) {
          const sess = JSON.parse(raw);
          console.log('🔍 useIntegracoes - Parsed session:', sess);
          empresaId = sess?.empresa?.id || sess?.usuario?.empresaId || '';
          userEmail = sess?.usuario?.email || sess?.usuario?.id || '';
          console.log('🔍 useIntegracoes - Extracted params:', { empresaId, userEmail });
        }
      } catch (err) {
        console.error('❌ Erro ao parsear sessão:', err);
      }

      const qs = new URLSearchParams();
      if (empresaId) qs.set('empresaId', empresaId);
      if (userEmail) qs.set('userEmail', userEmail);

      const url = apiUrl(`/integracoes?${qs.toString()}`);
      console.log('🔍 useIntegracoes - Fazendo requisição para:', url);
      const response = await fetch(url);
      console.log('🔍 useIntegracoes - Response status:', response.status);
      const data = await response.json();
      console.log('🔍 useIntegracoes - Response data:', data);

      if (data?.success && Array.isArray(data.data)) {
        console.log('🔍 useIntegracoes - Dados brutos recebidos:', JSON.stringify(data.data, null, 2));
        
        const integracoesConvertidas = data.data.map((integracao: any) => {
          console.log('🔄 Convertendo integração:', {
            raw: integracao,
            ativoOriginal: integracao.ativo,
            ativoConvertido: integracao.ativo !== false
          });
          
          return {
            id: integracao._id || integracao.id,
            nome: integracao.nome,
            tipo: integracao.tipo,
            email: integracao.configuracao?.email || integracao.configuracao?.emailDestino || '',
            emailDestino: integracao.configuracao?.emailDestino,
            pastaId: integracao.configuracao?.pastaId,
            pastaNome: integracao.configuracao?.pastaNome || integracao.configuracao?.nomeConfigurador || integracao.nome,
            pastaPath: integracao.configuracao?.pastaPath,
            ativo: integracao.ativo !== false,
            configuradoEm: integracao.criadoEm ? new Date(integracao.criadoEm) : (integracao.createdAt ? new Date(integracao.createdAt) : new Date()),
            ultimaSync: integracao.atualizadoEm ? new Date(integracao.atualizadoEm) : (integracao.updatedAt ? new Date(integracao.updatedAt) : undefined),
            nomeConfigurador: integracao.configuracao?.nomeConfigurador,
            descricao: integracao.descricao,
            criadoPor: integracao.criadoPor,
            credenciais: integracao.configuracao?.credenciais
          };
        });
        
        // Remove duplicados baseado no ID
        const integracoesUnicas = integracoesConvertidas.filter((integracao: IntegracaoConfig, index: number, self: IntegracaoConfig[]) =>
          index === self.findIndex((t: IntegracaoConfig) => t.id === integracao.id)
        );
        
        console.log(`📋 Carregadas ${integracoesConvertidas.length} integrações (${integracoesUnicas.length} únicas) do MongoDB Atlas:`, integracoesUnicas);
        setIntegracoes(integracoesUnicas);
      } else {
        console.error('Erro ao carregar integrações:', data?.error || data?.message);
        setIntegracoes([]);
      }
    } catch (error) {
      console.error('Erro ao conectar com MongoDB Atlas:', error);
      setIntegracoes([]);
    } finally {
      setLoading(false);
    }
  };

  const getIntegracoesAtivas = (): IntegracaoConfig[] => {
    const ativas = integracoes.filter((int: IntegracaoConfig) => int.ativo);
    console.log(`🔍 getIntegracoesAtivas - ${ativas.length} de ${integracoes.length} integrações ativas:`, ativas);
    return ativas;
  };

  const getIntegracaoPorId = (id: string): IntegracaoConfig | null => {
    return integracoes.find((int: IntegracaoConfig) => int.id === id) || null;
  };

  const enviarArquivoParaIntegracao = async (
    arquivo: File,
    integracaoId: string,
    nomeArquivo?: string
  ): Promise<{success: boolean; message: string; url?: string}> => {
    setLoading(true);
    try {
      const integracao = getIntegracaoPorId(integracaoId);
      if (!integracao) {
        throw new Error('Integração não encontrada');
      }

      if (integracao.tipo === 'email') {
        const fileDataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(arquivo);
        });

  const response = await fetch(apiUrl('/upload-arquivo'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            file: {
              name: nomeArquivo || arquivo.name,
              size: arquivo.size,
              type: arquivo.type,
              dataUrl: fileDataUrl
            },
            usuario: {
              nome: 'Usuário Sistema',
              email: 'usuario@sistema.com',
              empresa: 'Sistema Escalone'
            },
            emailDestino: integracao.emailDestino,
            nomeConfigurador: integracao.nome
          })
        });

        const result = await response.json();
        
        if (response.ok) {
          return {
            success: true,
            message: result.message,
            url: result.destino
          };
        } else {
          throw new Error(result.message || 'Erro no envio');
        }
      }

      throw new Error('Tipo de integração não suportado ainda');

    } catch (error) {
      console.error('Erro ao enviar arquivo:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erro desconhecido'
      };
    } finally {
      setLoading(false);
    }
  };

  const enviarMultiplosArquivosParaIntegracao = async (
    arquivos: Array<{ name: string; size: number; type: string; dataUrl: string }>,
    integracaoId: string
  ): Promise<{success: boolean; message: string; results: Array<{success: boolean; url?: string; fileName: string}>}> => {
    setLoading(true);
    try {
      const integracao = getIntegracaoPorId(integracaoId);
      if (!integracao) {
        throw new Error('Integração não encontrada');
      }

      const results = [];

      if (integracao.tipo === 'email') {
        // Para email, enviar todos os arquivos em um único email
        const response = await fetch(apiUrl('/upload-multiplos-arquivos'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            files: arquivos,
            usuario: {
              nome: 'Usuário Sistema',
              email: 'usuario@sistema.com',
              empresa: 'Sistema Escalone'
            },
            emailDestino: integracao.emailDestino,
            nomeConfigurador: integracao.nome
          })
        });

        const result = await response.json();
        
        if (response.ok) {
          return {
            success: true,
            message: result.message,
            results: arquivos.map(arquivo => ({
              success: true,
              fileName: arquivo.name,
              url: result.destino
            }))
          };
        } else {
          throw new Error(result.message || 'Erro no envio');
        }
      } else if (integracao.tipo === 'google_drive') {
        // Para Google Drive, fazer upload individual de cada arquivo
        for (const arquivo of arquivos) {
          try {
            // Converter dataUrl para Blob
            const response = await fetch(arquivo.dataUrl);
            const blob = await response.blob();
            const file = new File([blob], arquivo.name, { type: arquivo.type });

            // Criar FormData para envio
            const formData = new FormData();
            formData.append('files', file);
            if (integracao.pastaId) {
              formData.append('folderId', integracao.pastaId);
            }

            const uploadResponse = await fetch(apiUrl('/google-drive/upload-multiple'), {
              method: 'POST',
              body: formData
            });

            const uploadResult = await uploadResponse.json();

            if (uploadResponse.ok) {
              results.push({
                success: true,
                fileName: arquivo.name,
                url: uploadResult.files?.[0]?.webViewLink
              });
            } else {
              results.push({
                success: false,
                fileName: arquivo.name
              });
            }
          } catch (error) {
            console.error(`Erro ao enviar ${arquivo.name}:`, error);
            results.push({
              success: false,
              fileName: arquivo.name
            });
          }
        }

        const successCount = results.filter(r => r.success).length;
        return {
          success: successCount > 0,
          message: `${successCount} de ${arquivos.length} arquivo(s) enviado(s) com sucesso`,
          results
        };
      }

      throw new Error('Tipo de integração não suportado ainda');

    } catch (error) {
      console.error('Erro ao enviar arquivos:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Erro desconhecido',
        results: arquivos.map(arquivo => ({
          success: false,
          fileName: arquivo.name
        }))
      };
    } finally {
      setLoading(false);
    }
  };

  return {
    integracoes,
    loading,
    carregarIntegracoes,
    getIntegracoesAtivas,
    getIntegracaoPorId,
    enviarArquivoParaIntegracao,
    enviarMultiplosArquivosParaIntegracao
  };
};
